import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { ApiError } from "../client";
import { useApiClient } from "../context";
import type { components } from "../schema";

export type Worldline = components["schemas"]["Worldline"];
export type ForkWorldlineRequest = components["schemas"]["ForkWorldlineRequest"];

// fork 操作の入力型。どの世界線を起点に分岐するかを含める。
export type ForkWorldlineVariables = {
  worldlineId: string;
} & ForkWorldlineRequest;

export type UseForkWorldlineResult = UseMutationResult<
  Worldline,
  ApiError,
  ForkWorldlineVariables
>;

// POST /worldlines/{worldlineId}/fork を叩き、指定したシーン・選択肢を起点に
// 新しい世界線を作成する mutation。
// 成功時は worldlineTree キャッシュを invalidate して最新の世界線ツリーを
// 次回参照時に再取得させる。
// scenarioId を受け取り invalidate のスコープを絞る。
// null の場合は全ての worldlineTree キャッシュを invalidate する。
export function useForkWorldline(
  scenarioId: string | null,
): UseForkWorldlineResult {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<Worldline, ApiError, ForkWorldlineVariables>({
    mutationFn: async ({ worldlineId, ...body }) => {
      const { data, error } = await client.POST(
        "/worldlines/{worldlineId}/fork",
        {
          params: { path: { worldlineId } },
          body,
        },
      );

      if (error !== undefined) {
        // 通常は middleware が ApiError を throw するためここには到達しないが、
        // 型安全のための保険として明示的に throw する。
        throw new ApiError(
          typeof (error as { status?: number }).status === "number"
            ? (error as { status: number }).status
            : 0,
          error as unknown as ApiError["problem"],
        );
      }
      if (data === undefined) {
        throw new ApiError(500, {
          type: "about:blank",
          title: "Empty response",
          status: 500,
          detail: "forkWorldline returned no data",
        });
      }
      return data;
    },
    onSuccess: () => {
      // fork 後は世界線ツリーが変化するため、対象シナリオのキャッシュを無効化する。
      // scenarioId が未指定の場合はすべての worldlineTree を無効化する。
      void queryClient.invalidateQueries({
        queryKey:
          scenarioId !== null
            ? ["worldlineTree", scenarioId]
            : ["worldlineTree"],
      });
    },
  });
}
